/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * Weekly Recap share — modal with the generated PNG card and share/download buttons.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChartLineUpIcon, DownloadSimpleIcon, ShareNetworkIcon, XIcon, CopyIcon } from "@phosphor-icons/react";
import { api, BACKEND_URL } from "@/lib/api";

const RecapShareModal = ({ open, onClose, sessionId, channel }) => {
  const [recap, setRecap] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgUrl = `${BACKEND_URL}/api/sessions/${sessionId}/recap.png?channel=${encodeURIComponent(channel || "my channel")}&t=${Date.now()}`;
  const shareText = `My week with Lyra on @lucru_app — ${recap?.messages_read || 0} chat messages read aloud, ${recap?.highlights || 0} highlights. Try it free at ai-chat-live-13.emergent.host`;

  useEffect(() => {
    if (!open || !sessionId) return;
    api.get(`/sessions/${sessionId}/recap`).then((r) => setRecap(r.data)).catch(() => {});
  }, [open, sessionId]);

  const download = () => {
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = "lucru-recap.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Recap saved");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Caption copied — paste it with the image!");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      // Try Web Share API with the image
      const blob = await (await fetch(imgUrl)).blob();
      const file = new File([blob], "lucru-recap.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText, title: "My week with Lyra" });
        toast.success("Shared");
        return;
      }
      // Fallback: open Twitter intent
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
        "_blank",
        "noopener"
      );
    } catch (e) {
      toast.error("Share cancelled");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="recap-modal"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 22 }}
            className="glass rounded-2xl p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#222233]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ChartLineUpIcon size={16} color="#00E5FF" />
                <div className="font-display font-black text-sm uppercase tracking-widest">Your weekly recap</div>
              </div>
              <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="recap-close">
                <XIcon size={16} />
              </button>
            </div>
            <div className="rounded-xl overflow-hidden border border-[#222233] bg-[#05050A]">
              <img src={imgUrl} alt="Weekly recap" className="w-full block" data-testid="recap-image" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={download} className="btn-pill btn-ghost text-xs justify-center" data-testid="recap-download">
                <DownloadSimpleIcon size={13} /> Save
              </button>
              <button onClick={copyText} className="btn-pill btn-ghost text-xs justify-center" data-testid="recap-copy-text">
                <CopyIcon size={13} /> Caption
              </button>
              <button onClick={nativeShare} disabled={busy} className="btn-pill btn-primary text-xs justify-center" data-testid="recap-share">
                <ShareNetworkIcon size={13} /> Share
              </button>
            </div>
            {recap && (
              <div className="mt-3 text-[11px] text-white/50 text-center">
                {recap.total_messages} msgs received · {recap.messages_read} read aloud · {recap.highlights} highlights
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecapShareModal;
